/** @import { ComponentType, Context, VNode } from "preact"; */
import { computed, effect, signal } from "@preact/signals";
/** @import { Signal } from "@preact/signals"; */
import { Component, createContext, h } from "preact";

/**
 * Runs `fn` inside a view transition, if supported.
 *
 * @param {ViewTransitionUpdateCallback} fn
 */
export function transition(fn) {
	if (!document.startViewTransition) {
		fn();
		return;
	}

	const t = document.startViewTransition(fn);
	t.ready.catch(() => {});
	t.finished.catch(() => {});
}

/**
 * Navigate to a new route.
 *
 * @param {string} to
 * @param {object} [options]
 * @param {boolean} [options.replace] Don't allow navigating back to the previous route.
 * @param {boolean} [options.animate] Animate the transition between routes.
 * @param {Record<string, unknown>} [options.state]
 */
export function navigate(to, { replace, animate, state } = {}) {
	const dest = new URL(to, location.origin);

	history.replaceState({ animate }, "", null);
	if (!animate) url.value = dest;
	else {
		transition(() => {
			url.value = dest;
		});
	}

	if (replace) history.replaceState({ animate, ...state }, "", formatUrl(to));
	else history.pushState({ animate, ...state }, "", formatUrl(to));
}

/**
 * Format the URL before writing it to the address bar. Can be overridden to use nonstandard routing
 * behavior (such as hash routing).
 *
 * @param {string} url
 */
let formatUrl = url => url;

/**
 * Read the URL from the address bar. Can be overridden to use nonstandard routing behavior (such as
 * hash routing).
 */
export let readUrl = () => new URL(location.href);

/**
 * Replace the default routing behavior with "hash routing" (putting the entire path in the URL
 * hash). Useful for environments that won't serve `index.html` on 404s.
 */
export function useHashLocation() {
	// override the url formatting to mount the app as a hash at the initial pathname and query string
	formatUrl = url => `${location.pathname}${location.search}#${url}`;

	// override the url reading to use the hash
	readUrl = () => new URL(location.hash.slice(1), location.origin);

	// re-initialize the URL from the hash
	url.value = readUrl();
}

/**
 * @param {string} href
 * @param {MouseEvent} e
 * @returns {"spa" | "browser" | "none"}
 */
let linkHandler = (href, e) => {
	if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return "browser";
	if (e.button !== 0) return "browser";

	const url = new URL(href, location.origin);
	if (url.origin !== window.location.origin) return "browser";

	return "spa";
};

/**
 * Set a function that intercepts link clicks and returns a string indicating how navigation should
 * be handled: - "spa" handles the navigation using the in-app router - "browser" falls back to the
 * browser's default behavior - "none" prevents the navigation entirely
 *
 * @param {(href: string, e: MouseEvent) => "spa" | "browser" | "none"} fn
 */
export function setLinkHandler(fn) {
	linkHandler = fn;
}

/** The canonical current route of the app. */
const url = signal(readUrl()),
	urlSegments = computed(() => url.value.pathname.split("/").filter(seg => seg !== ""));

/** The pathname of the current route. */
export const pathname = computed(() => url.value.pathname || "/");

/** The search parameters of the current route. */
export const search = computed(() => Object.fromEntries(url.value.searchParams.entries()));

effect(() => {
	// whenever the pathname changes...
	pathname.value; // oxlint-disable-line no-unused-expressions

	// find the page's h1 and focus it
	const h1 = document.querySelector("h1");
	const tabIndex = h1?.getAttribute("tabindex");
	h1?.setAttribute("tabindex", tabIndex ?? "-1");
	h1?.focus();
});

// whenever the URL changes, update the route
addEventListener("popstate", e => {
	const animate = (e.state || { animate: false }).animate && !e.hasUAVisualTransition;

	if (!animate) url.value = readUrl();
	else {
		transition(() => {
			url.value = readUrl();
		});
	}
});

/**
 * Function to intercept click events originating from <a> tags and navigate to that rounte.
 *
 * @param {MouseEvent} e
 */
function handleClick(e) {
	// get the href of the link
	const target = /** @type {HTMLElement} */ (e.target),
		link = /** @type {HTMLAnchorElement | null} */ (target.closest("a[href]"));
	if (!link) return;

	// determine how the navigation will be handled
	const handler = linkHandler(link.href, e);

	// if the browser is handling it, fall back to its default behavior
	if (handler === "browser") return;
	// otherwise, we need to prevent the browser's default behavior
	e.preventDefault();

	// if the navigation is being prevented entirely, return
	if (handler === "none") return;

	const { pathname, search, hash } = new URL(link.href, location.origin);
	const animate = "animate" in (link?.dataset ?? {});
	navigate(pathname + search + hash, { animate });
}

addEventListener("click", handleClick);
addEventListener("auxclick", handleClick);

/**
 * The router context passed down through the component tree. Used for keeping track of the route
 * consumed by the current subtree, which varies depending on the subtree branch in which a
 * component exists. For example, this example component tree would have the following value for
 * `base`:
 *
 *     <Route path="/"> -> /
 *       <Route path="/foo"> -> /foo
 *         <Route path="/bar" /> -> /foo/bar
 *       </Route>
 *     </Route>
 *
 * **This should only be used for data that mirrors the shape of the tree!** Global data such as the
 * current route should go in top level "module" state.
 *
 * @typedef {object} RouterContext
 * @property {string[]} base The base path of the current subtree.
 */

/** @type {RouterContext} The default RouterContext value. */
const value = { base: [] };

/** @type {Context<RouterContext>} The RouterContext. */
const Ctx = createContext(value);

export class LocationProvider extends Component {
	render() {
		return h(Ctx.Provider, { value }, this.props.children);
	}
}

/**
 * @typedef {object} RouterProps
 * @property {VNode<RouteProps>[]} children
 */

/** @param {RouterProps} props */
export function Router(props) {
	return h(
		Ctx.Consumer,
		null,
		/** @param {RouterContext} router */ router => h(RouterSwitch, { ...props, router }),
	);
}

/**
 * @typedef {object} RouterSwitchProps
 * @property {RouterContext} router
 * @property {VNode<RouteProps>[]} children
 */

/**
 * Check if a route pattern matches the current URL segments.
 *
 * @param {string[]} pattern - The route pattern segments (may include :param segments)
 * @param {string[]} url - The current URL segments
 */
function match(pattern, url) {
	return pattern.every((patternSeg, i) => {
		const urlSeg = url[i];
		if (urlSeg === undefined) return false;

		// :param segments match any value
		if (patternSeg.startsWith(":")) return true;

		// otherwise, must match exactly
		return patternSeg === urlSeg;
	});
}

/**
 * Compare two matched route patterns. Longer paths win; on ties, the path with more concrete (non-
 * `:param`) segments wins. Returns >0 if `a` is more specific than `b`.
 *
 * @param {string[]} a
 * @param {string[]} b
 */
function compareSpecificity(a, b) {
	if (a.length !== b.length) return a.length - b.length;
	const concrete = (/** @type {string[]} */ segs) =>
		segs.filter(seg => !seg.startsWith(":")).length;
	return concrete(a) - concrete(b);
}

/** @param {RouterSwitchProps} props */
function RouterSwitch(props) {
	/** @type {{ child: VNode<RouteProps>; absPath: string[] } | undefined} */
	let bestMatch;

	for (const child of props.children) {
		// prepend the router base path to the child's relative path to obtain the child's absolute path
		const base = props.router.base,
			end = child.props.path.split("/").filter(seg => seg !== ""),
			absPath = base.concat(end);

		// check if the route matches
		if (!match(absPath, urlSegments.value)) continue;

		// if route matches, keep it if it's more specific than the current best
		if (!bestMatch || compareSpecificity(absPath, bestMatch.absPath) > 0) {
			bestMatch = { child, absPath };
		}
	}

	if (!bestMatch) return null;

	// override the context value's base path for any descendants with child's absolute path
	const value = { ...props.router, base: bestMatch.absPath };
	return h(Ctx.Provider, { value }, bestMatch.child);
}

/**
 * @template [P ={}]
 * @typedef {{ params: Signal<P & Record<string, string>> }} RouteParams
 */

/**
 * @template [P ={}]
 * @typedef {object} RouteProps
 * @property {string} path
 * @property {ComponentType<P | (P & RouteParams)>} component
 * @property {Omit<P, "params">} props
 */

/**
 * @template T
 * @extends {Component<RouteProps<T>>}
 */
export class Route extends Component {
	/** @override */
	static contextType = Ctx;

	/** @type {Signal<Record<string, string>>} */
	params = signal({});
	computedParams = computed(() => this.params.value);

	render() {
		const { component, props } = this.props;
		// compute params from the matched pattern (context.base) and current URL
		const params = /** @type {Record<string, string>} */ ({});
		for (let i = 0; i < this.context.base.length; i++) {
			const seg = this.context.base[i];
			if (seg?.startsWith(":")) params[seg.slice(1)] = urlSegments.value[i] ?? "";
		}
		this.params.value = params;

		return h(component, /** @type {T & RouteParams} */ ({ ...props, params: this.computedParams }));
	}
}
